import { ColoredIcon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { Timeline, TimelineWeeklyControls, useTimelineWeeklyControls } from "@msuite/ui/timeline";
import type { PartnerTimelineAssignmentReference } from "@msuite/utils/types";
import { AnimatePresence } from "motion/react";
import { useState } from "react";
import { usePartnerTimeline } from "@/hooks/use-partner-timeline";
import { PartnerTimelineInvoiceTracking } from "../partner-timeline-invoice-tracking";
import { PartnerTimelineReports } from "../partner-timeline-reports";
import type { PartnerPanelBaseProps } from "../types";
import { PartnerTimelineAssignmentReferenceItem } from "./partner-timeline-assignment-reference-item";
import { PartnerTimelineInformation } from "./partner-timeline-information";

export function PartnerTimeline({ partnershipId, orgId }: PartnerPanelBaseProps) {
	/** Hooks */
	const { currentWeek } = useTimelineWeeklyControls();
	const { t } = useTranslation();

	/** Query */
	const { data, isLoading, refetch, queryKey } = usePartnerTimeline({ partnershipId, currentWeek });

	const [value, setValue] = useState<"reports" | "timeline" | "invoice-tracking">("timeline");

	/** Render */
	return (
		<Panels>
			<Panels.Panel flex={1}>
				<SegmentedControl
					value={value}
					onChange={setValue}
					items={[
						{
							value: "timeline",
							label: t("timeline.timeline"),
							cyId: "partner-timeline-timeline-tab",
						},
						{
							value: "invoice-tracking",
							label: t("partners.reports.invoice_tracking"),
							cyId: "partner-timeline-invoice-tracking-tab",
						},
						{
							value: "reports",
							label: t("partners.reports.reports"),
							cyId: "partner-timeline-reports-tab",
						},
					]}
				/>
				<AnimatePresence mode="wait">
					<PartnerTimelineInformation
						key={`partner-timeline-information-${currentWeek}`}
						currentWeek={currentWeek}
						partnershipId={partnershipId}
					/>
				</AnimatePresence>
			</Panels.Panel>
			<Panels.Panel flex={2.5}>
				{value === "timeline" && (
					<Timeline
						timeline={data?.timeline}
						showEmptySections
						controls={<TimelineWeeklyControls queryKey={queryKey} />}
						renderItemMarker={() => (
							<ColoredIcon
								icon={icon("Calendar")}
								color="emerald"
							/>
						)}
						renderItem={(_, item) => (
							<PartnerTimelineAssignmentReferenceItem
								item={item as PartnerTimelineAssignmentReference}
								partnershipId={partnershipId}
							/>
						)}
						isLoading={isLoading}
						refetch={refetch}
					/>
				)}
				{value === "reports" && (
					<PartnerTimelineReports
						orgId={orgId}
						partnershipId={partnershipId}
					/>
				)}
				{value === "invoice-tracking" && (
					<PartnerTimelineInvoiceTracking
						orgId={orgId}
						partnershipId={partnershipId}
					/>
				)}
			</Panels.Panel>
		</Panels>
	);
}
